/**
 * The shelf: the first thing anyone sees, and the screen they come back to.
 *
 * The order on the page is the order of use. Continue first, because reopening
 * yesterday's book is what most visits are. Filters next, because narrowing is
 * the second thing anyone does. Then the grid, which is the library itself and
 * gets the rest of the room.
 *
 * The header holds a name and a search field and nothing else. Everything a
 * toolbar would have offered is either in the filter chips or does not belong on
 * a reading surface at all.
 */

import { useCallback, useEffect, useState } from "react";

import { BookCard, BookCardSkeleton } from "./BookCard";
import { BookRow, BookRowSkeleton } from "./BookRow";
import { ContinueRow } from "./ContinueRow";
import { FilterBar } from "./FilterBar";
import { PreparingRow, useQueue } from "./PreparingRow";
import { Mark, SearchIcon } from "../components/icons";
import { SearchPanel } from "./SearchPanel";
import { ViewToggle } from "./ViewToggle";
import { Button } from "../components/ui";
import { plural, t, useUiLang } from "../i18n";
import {
  PAGE_SIZE,
  countActive,
  useContinue,
  useFacets,
  useFilters,
  useLibraryBooks,
  useSentinel,
  useViewMode,
} from "./useLibrary";

const GRID =
  "grid grid-cols-2 gap-x-4 gap-y-8 sm:grid-cols-3 sm:gap-x-5 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6";

// One column of rows. No separator between them: every row already ends with
// its own progress track, and a second hairline underneath made the two read as
// one ambiguous rule. Capped at a readable measure on a
// wide screen: a title on the left and a percentage 1600px to its right is two
// facts the eye cannot hold at once.
const LIST = "mx-auto flex max-w-3xl flex-col gap-0.5";

export function LibraryScreen({
  user,
  onSignOut,
}: {
  /** Null when the server has no password configured: there is then nothing to
      sign out of, and a button offering it would be a lie. */
  user: string | null;
  onSignOut: () => Promise<void>;
}) {
  useUiLang();
  const { filters, patch, reset } = useFilters();
  const facets = useFacets();
  const { books, total, loading, loadingMore, error, offline, hasMore, loadMore } =
    useLibraryBooks(filters);
  const carryOn = useContinue();
  const { queue, drop } = useQueue();
  const { view, setView } = useViewMode();
  const [searchOpen, setSearchOpen] = useState(false);

  // A book halfway through its chain is in the catalogue, and therefore on the
  // shelf, with a second of silence for a recording. It belongs in the strip
  // above with its progress, not in the grid pretending to be readable.
  const building = new Set(
    queue.jobs
      .filter((job) => job.state === "queued" || job.state === "running")
      .map((job) => job.book_id)
      .filter(Boolean),
  );
  const shelf = building.size === 0 ? books : books.filter((book) => !building.has(book.id));

  const sentinel = useSentinel(hasMore, loadMore);
  const closeSearch = useCallback(() => setSearchOpen(false), []);

  // `/` is the one global shortcut. It is swallowed when the user is already
  // typing somewhere, otherwise it eats slashes in every field on the page.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "/" || event.metaKey || event.ctrlKey || event.altKey) return;
      const target = event.target;
      if (
        target instanceof HTMLElement &&
        (target.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName))
      ) {
        return;
      }
      event.preventDefault();
      setSearchOpen(true);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const filtered = countActive(filters) > 0;
  const nothing = !loading && books.length === 0;

  return (
    <div className="min-h-full bg-[var(--color-page)]">
      <header className="sticky top-0 z-30 border-b border-[var(--color-line)] bg-[var(--color-page)]/85 backdrop-blur-md">
        <div className="mx-auto flex max-w-fib1597 items-center gap-3 px-4 py-3.5 sm:gap-4 sm:px-8">
          {/* The mark alone, at every width. The name is in the tab, the title
              and the label; repeating it across the top of a phone spent a
              tenth of the line on a word the reader had already read to get
              here. The negative margin keeps the tap target at 34px without
              the padding pushing the search field along. */}
          <a
            href="#/"
            title="ReadCommons"
            aria-label={t("libHome")}
            className="tap -m-2 flex shrink-0 items-center rounded-lg p-2 text-[var(--color-accent)] transition-opacity duration-150 hover:opacity-75"
          >
            <Mark className="size-fib21" />
          </a>
          {/* Search asks the server, so offline it can only ever come back
              empty handed. The field goes away rather than lie; the spacer
              keeps everything to its right where it was. */}
          {offline ? (
            <span className="ml-auto" />
          ) : (
            <button
              onClick={() => setSearchOpen(true)}
              aria-label={t("searchLibrary")}
              className="tap ml-auto flex w-full max-w-sm items-center gap-2.5 rounded-lg border border-[var(--color-line)] bg-[var(--color-surface)] px-3 py-2 text-left text-sm text-[var(--color-faint)] transition-colors duration-150 hover:border-[var(--color-muted)] hover:text-[var(--color-muted)]"
            >
              <SearchIcon className="h-4 w-4 shrink-0" />
              <span className="truncate">{t("libSearch")}</span>
              <kbd className="ml-auto hidden shrink-0 rounded border border-[var(--color-line)] px-1.5 py-0.5 font-[family-name:var(--font-mono)] text-2xs sm:block">
                /
              </kbd>
            </button>
          )}
          {user && (
            // Icon only, at every width: the header is a single line on a phone
            // and a name plus a word would be the thing that breaks it. The
            // name is in the tooltip, and the padding carries the tap target.
            <button
              onClick={() => void onSignOut()}
              title={t("libSignedInAs", { user })}
              aria-label={t("libSignedInAs", { user })}
              className="tap shrink-0 rounded-lg p-2.5 text-[var(--color-faint)] transition-colors duration-150 hover:bg-[var(--color-surface)] hover:text-[var(--color-ink)]"
            >
              <svg
                viewBox="0 0 24 24"
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <path d="M16 17l5-5-5-5" />
                <path d="M21 12H9" />
              </svg>
            </button>
          )}
        </div>
      </header>

      <main className="mx-auto max-w-fib1597 px-4 pb-[calc(6rem+env(safe-area-inset-bottom))] pt-8 sm:px-8 sm:pt-10">
        <PreparingRow queue={queue} onDrop={drop} />

        <ContinueRow books={carryOn.books} loading={carryOn.loading} />

        <section aria-labelledby="shelf-heading">
          <div className="mb-5 flex items-center justify-between gap-3">
            <h2
              id="shelf-heading"
              className="text-2xs font-medium uppercase tracking-[0.16em] text-[var(--color-muted)]"
            >
              {t("libTitle")}
            </h2>
            <div className="flex items-center gap-3">
              {!loading && (
                <span className="text-2xs text-[var(--color-faint)]">
                  {plural(total, "libBookOne", "libBookMany")}
                </span>
              )}
              <ViewToggle view={view} onChange={setView} />
            </div>
          </div>

          {/* No filters offline. The shelf is the offline cache, whole, and a
              row of chips that cannot narrow it is a row of promises the
              screen cannot keep. */}
          {!offline && (
            <div className="mb-6 sm:mb-8">
              <FilterBar
                filters={filters}
                facets={facets}
                total={total}
                onChange={patch}
                onReset={reset}
              />
            </div>
          )}

          {error && (
            <p
              role="alert"
              className="mb-6 rounded-lg bg-[var(--color-surface)] px-4 py-3 text-sm text-[var(--color-ink)]"
            >
              {error}
            </p>
          )}

          {/* A sober strip, not an error: the network is missing, and the shelf
              below is whatever survives that, kept books or none. */}
          {offline && (
            <p
              role="status"
              className="mb-6 rounded-lg bg-[var(--color-surface)] px-4 py-3 text-sm text-[var(--color-ink)]"
            >
              {t("libOffline")}
            </p>
          )}

          {loading && (
            <div
              className={view === "list" ? LIST : GRID}
              aria-busy="true"
              aria-label={t("libLoading")}
            >
              {Array.from({ length: 12 }, (_, slot) =>
                view === "list" ? <BookRowSkeleton key={slot} /> : <BookCardSkeleton key={slot} />,
              )}
            </div>
          )}

          {nothing && !error && (
            <div className="flex flex-col items-center gap-3 py-24 text-center">
              <p className="text-sm text-[var(--color-ink)]">
                {offline ? t("libOfflineEmptyTitle") : filtered ? t("libNoMatch") : t("libEmpty")}
              </p>
              <p className="max-w-sm text-xs leading-relaxed text-[var(--color-muted)]">
                {offline ? t("libOfflineEmptyHint") : filtered ? t("libNoMatchHint") : t("libEmptyHint")}
              </p>
              {/* Clearing filters solves nothing while offline: the shelf is the
                  whole offline cache already, filters or not. */}
              {filtered && !offline && (
                <Button variant="plain" onClick={reset}>
                  {t("libClearFilters")}
                </Button>
              )}
            </div>
          )}

          {!loading && shelf.length > 0 && (
            <>
              <div className={view === "list" ? LIST : GRID}>
                {shelf.map((book) =>
                  view === "list" ? (
                    <BookRow key={book.id} book={book} />
                  ) : (
                    <BookCard key={book.id} book={book} />
                  ),
                )}
              </div>

              {/* The next page is asked for when this comes near the viewport, so
                  the bottom of the shelf never shows a hole. */}
              <div ref={sentinel} className="h-px w-full" aria-hidden="true" />

              {loadingMore && (
                <div
                  className={view === "list" ? `${LIST} mt-2` : `${GRID} mt-8`}
                  aria-hidden="true"
                >
                  {Array.from({ length: Math.min(PAGE_SIZE, 6) }, (_, slot) =>
                    view === "list" ? (
                      <BookRowSkeleton key={slot} />
                    ) : (
                      <BookCardSkeleton key={slot} />
                    ),
                  )}
                </div>
              )}

              {!hasMore && total > PAGE_SIZE && (
                <p className="mt-12 text-center text-2xs text-[var(--color-faint)]">
                  {t("libAllOf", { count: total })}
                </p>
              )}
            </>
          )}
        </section>
      </main>

      <SearchPanel open={searchOpen} initialTerm={filters.q} onClose={closeSearch} />
    </div>
  );
}
